/**
 * The deterministic prompt test runner.
 *
 * For every (commit × test case) pair it interpolates the prompt, calls the
 * configured provider, then grades the response with the row's pure
 * assertions. The result is a scorecard per commit — e.g. `9/10 Passed · 90%`.
 */

import { evaluateAssertion } from './assertions';
import { interpolate } from './interpolate';
import { callCompletion } from './providers';
import type { Commit, RunResult, Settings, TestCase, CaseRunRow } from './types';
import { percent } from './util';

export interface RunInput {
  repoId: string;
  /** One commit for a single run, two for a side-by-side A/B regression run. */
  commits: Commit[];
  cases: TestCase[];
  settings: Settings;
  /** Force mock latency (tests pass 0 for instant runs). */
  delayMs?: number;
  /** Called after each row finishes. */
  onProgress?: (completed: number, total: number) => void;
}

function gradeRow(
  testCase: TestCase,
  output: string,
  latencyMs: number,
  error?: string
): CaseRunRow {
  const specs = testCase.assertions ?? [];
  const results = specs.map((spec) => {
    const outcome = evaluateAssertion(spec, output);
    return error ? { ...outcome, detail: `Provider error — ${error}` } : outcome;
  });
  const passed = results.filter((r) => r.pass).length;
  const pass = results.length === 0 ? true : passed === results.length;

  return {
    caseId: testCase.id,
    caseName: testCase.name,
    output,
    latencyMs,
    error,
    results,
    pass,
  };
}

/** Run the whole matrix across every requested commit. */
export async function runSuite(input: RunInput): Promise<Record<string, RunResult>> {
  const { repoId, commits, cases, settings, delayMs, onProgress } = input;
  const totalTasks = commits.length * cases.length;
  let completed = 0;

  const results: Record<string, RunResult> = {};

  for (const commit of commits) {
    const startedAt = Date.now();
    const rows: CaseRunRow[] = [];

    for (const testCase of cases) {
      const { output: prompt } = interpolate(commit.content, testCase.values ?? {});
      const seed = `${repoId}:${commit.id}:${testCase.id}`;

      let row: CaseRunRow;
      try {
        const completion = await callCompletion(
          { prompt },
          settings,
          delayMs !== undefined ? { seed, delayMs } : { seed }
        );
        row = gradeRow(testCase, completion.output, completion.latencyMs);
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        row = gradeRow(testCase, '', 0, message);
      }

      rows.push(row);
      completed++;
      onProgress?.(completed, totalTasks);
    }

    const total = rows.reduce((sum, row) => sum + row.results.length, 0);
    const passed = rows.reduce((sum, row) => sum + row.results.filter((r) => r.pass).length, 0);

    results[commit.id] = {
      commitId: commit.id,
      version: commit.version,
      provider: settings.provider,
      startedAt,
      durationMs: Date.now() - startedAt,
      rows,
      passed,
      total,
      score: percent(passed, total),
    };
  }

  return results;
}

export interface CompareSummary {
  left: RunResult;
  right: RunResult;
  /** Positive = right commit improved on left. */
  deltaAssertions: number;
  deltaScore: number;
  winner: 'left' | 'right' | 'tie';
}

/** Head-to-head verdict between two commits (used by the A/B scorecard). */
export function compareRuns(left: RunResult, right: RunResult): CompareSummary {
  const deltaAssertions = right.passed - left.passed;
  const deltaScore = right.score - left.score;
  const winner =
    deltaAssertions > 0 ? 'right' : deltaAssertions < 0 ? 'left' : 'tie';
  return { left, right, deltaAssertions, deltaScore, winner };
}
